/**
 * ISO 4217 List One currency and fund codes that define a numeric minor unit.
 * Source: SIX ISO 4217 Maintenance Agency, published 2026-01-01.
 * https://www.six-group.com/dam/download/financial-information/data-center/iso-currrency/lists/list-one.xml
 *
 * Codes whose official minor unit is "N.A." are intentionally unsupported: an
 * integer hourly-rate contract cannot represent them without another unit rule.
 */
export const ISO_4217_MINOR_UNIT_EXPONENTS = Object.freeze({
    AED: 2,
    AFN: 2,
    ALL: 2,
    AMD: 2,
    AOA: 2,
    ARS: 2,
    AUD: 2,
    AWG: 2,
    AZN: 2,
    BAM: 2,
    BBD: 2,
    BDT: 2,
    BHD: 3,
    BIF: 0,
    BMD: 2,
    BND: 2,
    BOB: 2,
    BOV: 2,
    BRL: 2,
    BSD: 2,
    BTN: 2,
    BWP: 2,
    BYN: 2,
    BZD: 2,
    CAD: 2,
    CDF: 2,
    CHE: 2,
    CHF: 2,
    CHW: 2,
    CLF: 4,
    CLP: 0,
    CNY: 2,
    COP: 2,
    COU: 2,
    CRC: 2,
    CUP: 2,
    CVE: 2,
    CZK: 2,
    DJF: 0,
    DKK: 2,
    DOP: 2,
    DZD: 2,
    EGP: 2,
    ERN: 2,
    ETB: 2,
    EUR: 2,
    FJD: 2,
    FKP: 2,
    GBP: 2,
    GEL: 2,
    GHS: 2,
    GIP: 2,
    GMD: 2,
    GNF: 0,
    GTQ: 2,
    GYD: 2,
    HKD: 2,
    HNL: 2,
    HTG: 2,
    HUF: 2,
    IDR: 2,
    ILS: 2,
    INR: 2,
    IQD: 3,
    IRR: 2,
    ISK: 0,
    JMD: 2,
    JOD: 3,
    JPY: 0,
    KES: 2,
    KGS: 2,
    KHR: 2,
    KMF: 0,
    KPW: 2,
    KRW: 0,
    KWD: 3,
    KYD: 2,
    KZT: 2,
    LAK: 2,
    LBP: 2,
    LKR: 2,
    LRD: 2,
    LSL: 2,
    LYD: 3,
    MAD: 2,
    MDL: 2,
    MGA: 2,
    MKD: 2,
    MMK: 2,
    MNT: 2,
    MOP: 2,
    MRU: 2,
    MUR: 2,
    MVR: 2,
    MWK: 2,
    MXN: 2,
    MXV: 2,
    MYR: 2,
    MZN: 2,
    NAD: 2,
    NGN: 2,
    NIO: 2,
    NOK: 2,
    NPR: 2,
    NZD: 2,
    OMR: 3,
    PAB: 2,
    PEN: 2,
    PGK: 2,
    PHP: 2,
    PKR: 2,
    PLN: 2,
    PYG: 0,
    QAR: 2,
    RON: 2,
    RSD: 2,
    RUB: 2,
    RWF: 0,
    SAR: 2,
    SBD: 2,
    SCR: 2,
    SDG: 2,
    SEK: 2,
    SGD: 2,
    SHP: 2,
    SLE: 2,
    SOS: 2,
    SRD: 2,
    SSP: 2,
    STN: 2,
    SVC: 2,
    SYP: 2,
    SZL: 2,
    THB: 2,
    TJS: 2,
    TMT: 2,
    TND: 3,
    TOP: 2,
    TRY: 2,
    TTD: 2,
    TWD: 2,
    TZS: 2,
    UAH: 2,
    UGX: 0,
    USD: 2,
    USN: 2,
    UYI: 0,
    UYU: 2,
    UYW: 4,
    UZS: 2,
    VED: 2,
    VES: 2,
    VND: 0,
    VUV: 0,
    WST: 2,
    XAD: 2,
    XAF: 0,
    XCD: 2,
    XCG: 2,
    XOF: 0,
    XPF: 0,
    YER: 2,
    ZAR: 2,
    ZMW: 2,
    ZWG: 2,
});
export const SUPPORTED_CURRENCY_CODES = Object.freeze(Object.keys(ISO_4217_MINOR_UNIT_EXPONENTS));
export function currencyMinorUnitExponent(currency) {
    const value = ISO_4217_MINOR_UNIT_EXPONENTS[currency];
    return typeof value === "number" ? value : null;
}
/**
 * Records an amount from accepted whole seconds and an employer-set hourly
 * rate. This is the sole half-away-from-zero minor-unit rounding boundary.
 */
export function recordedAmountMinor(acceptedSeconds, hourlyRateMinor) {
    if (hourlyRateMinor <= 0n)
        throw new RangeError("Hourly rate must be positive.");
    const product = acceptedSeconds * hourlyRateMinor;
    const sign = product < 0n ? -1n : 1n;
    return sign * ((product * sign + 1800n) / 3600n);
}
/** Converts an exact decimal major-unit entry into an integer minor-unit string. */
export function majorUnitsToMinorUnits(value, currency) {
    const exponent = currencyMinorUnitExponent(currency);
    if (exponent === null)
        return null;
    const match = /^(0|[1-9]\d*)(?:\.(\d+))?$/.exec(value.trim());
    if (!match)
        return null;
    const fraction = match[2] ?? "";
    if (fraction.length > exponent || (exponent === 0 && fraction.length > 0))
        return null;
    const scale = 10n ** BigInt(exponent);
    const paddedFraction = fraction.padEnd(exponent, "0");
    return (BigInt(match[1] ?? "0") * scale + BigInt(paddedFraction || "0")).toString();
}
/** Converts an integer minor-unit amount into an exact, unlocalized decimal. */
export function minorUnitsToMajorUnits(value, currency) {
    const exponent = currencyMinorUnitExponent(currency);
    if (exponent === null || !/^-?\d+$/.test(value))
        return null;
    const negative = value.startsWith("-");
    const digits = (negative ? value.slice(1) : value).replace(/^0+(?=\d)/, "");
    if (exponent === 0)
        return `${negative ? "-" : ""}${digits}`;
    const padded = digits.padStart(exponent + 1, "0");
    const whole = padded.slice(0, -exponent);
    const fraction = padded.slice(-exponent);
    return `${negative ? "-" : ""}${whole}.${fraction}`;
}
/** Formats integer minor units without first coercing the amount to a float. */
export function formatCurrencyMinorUnits(value, currency, locale) {
    const exponent = currencyMinorUnitExponent(currency);
    const major = minorUnitsToMajorUnits(value, currency);
    if (exponent === null || major === null)
        return `${value} ${currency} minor units`;
    try {
        const formatter = new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            minimumFractionDigits: exponent,
            maximumFractionDigits: exponent,
        });
        // Intl.NumberFormat's runtime accepts decimal strings and preserves their
        // mathematical value; using a string avoids losing large bigint precision.
        return formatter.format(major);
    }
    catch {
        return `${major} ${currency}`;
    }
}
//# sourceMappingURL=currency.js.map